import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useState } from "react";

import { CanvasButton } from "@/components/canvas-editor/components/Inputs";
import { preventBubble } from "@/components/canvas-editor/utils";
import { Spinner } from "@/components/Spinner";
import { Avatar } from "@/components/ui/avatar";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { searchStarterPacksQueryOptions } from "@/lib/api";
import { atUriToBskyUrl } from "@/lib/utils";

export function SearchListButton({
  selectUrl,
  type,
}: {
  selectUrl: (url: string) => void;
  type: "starter-pack" | "list";
}) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const debouncedTerm = useDebounce(term, 500);
  const { data: lists, isPending } = useQuery(
    searchStarterPacksQueryOptions({ term: debouncedTerm, type }),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <SimpleTooltip message="Search for a starter pack">
        <PopoverTrigger asChild>
          <CanvasButton size="icon" variant="canvas">
            <span className="sr-only">Search lists</span>
            <MagnifyingGlassIcon />
          </CanvasButton>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent className="p-0" {...preventBubble}>
        <Command>
          <CommandInput
            placeholder="Search starter packs"
            onValueChange={setTerm}
          />
          <CommandList>
            {!debouncedTerm ? (
              <div className="px-3 py-2 text-slate-dim">
                Enter a search term
              </div>
            ) : isPending ? (
              <div className="h-10 w-full flex items-center justify-center text-slate-dim">
                <Spinner />
              </div>
            ) : (
              <CommandGroup>
                {lists?.map((list) => {
                  const record = "record" in list ? list.record : list;

                  return (
                    <CommandItem
                      key={`${record.list}`}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                      value={`${record.list || (record as any).uri}-${
                        list.term
                      }`}
                      onSelect={() => {
                        selectUrl(
                          atUriToBskyUrl(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                            (list as any).uri || (record as any).uri,
                          ),
                        );
                        setOpen(false);
                      }}
                    >
                      <Avatar
                        src={list.list?.avatar || list.creator.avatar}
                        alt={list.list?.name || list.creator.handle}
                      />
                      <div className="flex flex-col gap-1 flex-1">
                        <div className="flex justify-between items-center">
                          <div className="">{record.name}</div>
                          <div className="text-xs px-1 py-0.5 bg-slate-3 dark:bg-slatedark-4">
                            {list.list?.list_item_count ||
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                              (record as any).list_item_count}
                          </div>
                        </div>
                        {record.description && (
                          <div className="text-slate-dim text-xs">
                            {record.description}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
